import { CopyableTableCell } from "@/components/typography/dfp-typography";
import { HealthScorePill } from "@/layouts/signed-in/HealthScorePill/HealthScorePill";
import { parseDateToUTCString } from "@/utils/transforms";
import { Skeleton, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { BsShieldFillCheck, BsShieldFillMinus } from "react-icons/bs";
import { State } from "./AssetsTable";

/** Health score color logic */
const getHealthColor = (score: number) => {
	if (score >= 75) return "#3f6e01";
	if (score >= 25) return "#e0a724";
	return "#bf1e2d";
};

/** Smaller font render for secondary detail columns */
const SmallText = ({ text, isLoading }: { text: string; isLoading?: boolean }) => {
	if (isLoading) return <Skeleton.Input style={{ width: 100 }} active size="small" />;
	return (
		<span style={{ fontSize: 12, color: "#595959", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
			{text || "N/A"}
		</span>
	);
};

export const initialColumns: ColumnsType = [
	{
		title: "Asset Tag",
		dataIndex: "assetTag",
		key: "assetTag",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		fixed: "left",
		render: (text, record) => {
			if (record.isLoading) {
				return <Skeleton.Input style={{ width: 120 }} active size="small" />;
			}
			const healthScore = record.healthScore?.healthScore ?? 0;
			const hasWarranty = record.warranty?.hasWarranty;
			const borderColor = getHealthColor(healthScore);

			const isVirtual = record.tagType === "VIRTUAL";
			const childCount = record.childCount ?? record.children?.length ?? 0;

			return (
				<Tooltip
					title={
						<div>
							<div>Health Score: <strong>{Math.round(healthScore)}</strong></div>
							<div>Warranty: <strong>{hasWarranty ? "Active" : "None"}</strong></div>
							{isVirtual && <div>Virtually tagged (no physical tag)</div>}
							{childCount > 0 && (
								<div>Child assets: <strong>{childCount}</strong></div>
							)}
						</div>
					}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							borderLeft: `4px solid ${borderColor}`,
							paddingLeft: 8,
							marginLeft: -12,
						}}
					>
						<span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{text || "N/A"}
						</span>
						{isVirtual && (
							<Tag
								color="purple"
								style={{ fontSize: 10, lineHeight: "16px", padding: "0 4px", marginInlineEnd: 0, flexShrink: 0 }}
							>
								Virtual
							</Tag>
						)}
						{childCount > 0 && (
							<Tag
								color="blue"
								style={{ fontSize: 10, lineHeight: "16px", padding: "0 4px", marginInlineEnd: 0, flexShrink: 0 }}
							>
								{childCount} {childCount === 1 ? "child" : "children"}
							</Tag>
						)}
						{hasWarranty ? (
							<BsShieldFillCheck style={{ color: "#52c41a", fontSize: 13, flexShrink: 0 }} />
						) : (
							<BsShieldFillMinus style={{ color: "#d9d9d9", fontSize: 13, flexShrink: 0 }} />
						)}
					</div>
				</Tooltip>
			);
		},
		width: 220,
	},
	{
		title: "Store #",
		dataIndex: ["location", "name"],
		key: "location_name",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<CopyableTableCell text={text || "N/A"} isLoading={record.isLoading} />
		),
		width: 200,
		ellipsis: true,
	},
	{
		title: "Asset Type",
		dataIndex: ["assetType", "name"],
		key: "asset_type_name",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<CopyableTableCell text={text || "N/A"} isLoading={record.isLoading} />
		),
		width: 180,
		ellipsis: true,
	},
	{
		title: "Manufacturer",
		dataIndex: ["manufacturer", "name"],
		key: "manufacturer",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<SmallText text={text || "N/A"} isLoading={record.isLoading} />
		),
		width: 180,
		ellipsis: true,
	},
	{
		title: "Model",
		dataIndex: "modelNumber",
		key: "modelNumber",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<SmallText text={text || "N/A"} isLoading={record.isLoading} />
		),
		width: 160,
		ellipsis: true,
	},
	{
		title: "Serial #",
		dataIndex: "serialNumber",
		key: "serialNumber",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => (
			<SmallText text={text || "N/A"} isLoading={record.isLoading} />
		),
		width: 150,
		ellipsis: true,
	},
	// ── Hidden by default (toggle via column picker) ──
	{
		title: "Health Score",
		dataIndex: ["healthScore", "healthScore"],
		key: "health_score",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		width: 80,
		align: "center",
		render: (healthScore, record) =>
			record.isLoading ? (
				<Skeleton.Input style={{ width: 40 }} active size="small" />
			) : (
				<HealthScorePill healthScore={healthScore || 0} size="sm" />
			),
		hidden: true,
	},
	{
		title: "Condition",
		dataIndex: "condition",
		key: "condition",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => {
			if (record.isLoading)
				return <Skeleton.Input style={{ width: 80 }} active size="small" />;
			if (!text || text === "N/A") return <span style={{ color: "#bfbfbf" }}>N/A</span>;
			const colorMap: Record<string, string> = {
				Excellent: "green",
				Good: "blue",
				Fair: "orange",
				Poor: "red",
			};
			return <Tag color={colorMap[text] || "default"}>{text}</Tag>;
		},
		width: 110,
		align: "center",
		hidden: true,
	},
	{
		title: "Address",
		dataIndex: ["location", "address1"],
		key: "address1",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (text, record) => {
			const location = record.location;
			const fullAddress = location
				? `${location.address1 || ""}${location.address2 ? ", " + location.address2 : ""}`
				: "N/A";
			return (
				<CopyableTableCell text={fullAddress} isLoading={record.isLoading} />
			);
		},
		width: 220,
		ellipsis: true,
		hidden: true,
	},
	{
		title: "Warranty",
		dataIndex: ["warranty", "hasWarranty"],
		key: "warranty",
		render: (hasWarranty, record) => {
			if (record.isLoading)
				return (
					<Skeleton.Input
						style={{ minWidth: 50, width: 50 }}
						active
						size="small"
					/>
				);
			return (
				<Tooltip title={hasWarranty ? "Under Warranty" : "No Warranty"}>
					<div
						style={{
							height: "22px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{hasWarranty ? (
							<BsShieldFillCheck className="green assets-table-icon fs-4" />
						) : (
							<BsShieldFillMinus className="red assets-table-icon fs-4" />
						)}
					</div>
				</Tooltip>
			);
		},
		align: "center",
		width: 90,
		hidden: true,
	},
	{
		title: "Mfr Date",
		dataIndex: "manufacturerDate",
		key: "manufacturerDate",
		sorter: true,
		sortDirections: ["descend", "ascend", "descend"],
		render: (date: string, record) => {
			return (
				<CopyableTableCell
					text={parseDateToUTCString(date) || "N/A"}
					isLoading={record.isLoading}
				/>
			);
		},
		width: 120,
		hidden: true,
	},
	{
		title: "Category",
		dataIndex: ["assetTypeCategory", "name"],
		key: "asset_type_category",
		render: (text, record) => (
			<CopyableTableCell text={text || "N/A"} isLoading={record.isLoading} />
		),
		width: 150,
		ellipsis: true,
		hidden: true,
	},
	{
		title: "Temp Alert ID",
		dataIndex: "tempAlertTag",
		key: "tempAlertTag",
		render: (text, record) => (
			<CopyableTableCell text={text || "N/A"} isLoading={record.isLoading} />
		),
		width: 150,
		ellipsis: true,
		hidden: true,
	},
	{
		title: "Created Date",
		dataIndex: "createdTimestamp",
		key: "createdTimestamp",
		render: (date: string, record) => (
			<CopyableTableCell
				text={parseDateToUTCString(date) || "N/A"}
				isLoading={record.isLoading}
			/>
		),
		width: 130,
		hidden: true,
	},
];

export const getOrgColumns = (organization: any, state: State): ColumnsType => {
	const filterProps = {
		filters: state?.filterOptions?.assetTypes
			? state.filterOptions.assetTypes.map((type: any) => ({
					text: typeof type === "string" ? type : type.name,
					value: typeof type === "string" ? type : type.name,
				}))
			: undefined,
		filteredValue: state?.filters?.asset_type_name
			? state.filters.asset_type_name
			: undefined,
		filterSearch: true,
	};

	const columns: ColumnsType = initialColumns.map((column) => {
		if (column.key === "asset_type_name") {
			return { ...column, ...filterProps };
		}
		return column;
	});

	switch (organization.external_firebase_id) {
		default:
			return columns;
	}
};
